import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Field, FieldArray, submit } from 'redux-form';
import { Row, Col, Button } from 'reactstrap';
import Availability from '../../models/Availability';
import moment from 'moment';
import DateTimePicker from '../DateTimePicker/DateTimePicker';

import './AvailabilitiesField.css';

function phase_envelope_datetime_bounds(phases) {
    if (!phases?.length)
        return {};

    const starts = phases.map(p => moment(p.start_at)).filter((m) => m.isValid());
    const stops = phases.map(p => moment(p.stop_at)).filter((m) => m.isValid());
    if (!starts.length || !stops.length)
        return {};

    return {
        min: moment.min(starts).startOf('day').format('YYYY-MM-DDTHH:mm'),
        max: moment.max(stops).endOf('day').format('YYYY-MM-DDTHH:mm'),
    };
}

const AvailabilitiesField = ({ name, preventSubmit, phases, ..._rest }) => {
    const dispatch    = useDispatch();
    const { t } = useTranslation();

    const Component = ({ fields, meta: { form }}) => {

        const availabilities = (fields?.getAll() || []).map(props => new Availability(props));

        const remove = (index) => {
            fields.remove(index);
            !preventSubmit && setTimeout(() => dispatch(submit(form)), 10);
        };

        const add = (_availability) => {
            let start_at = null;
            let stop_at = null;

            const availabilities = (fields?.getAll() || []).map(props => new Availability(props));
            if (availabilities?.length > 0) {
                const last = availabilities[availabilities.length - 1];
                start_at = moment(last.start_at).add(1, 'day');
                stop_at = moment(last.stop_at).add(1, 'day');
            } else if (phases?.length) {
                // First added availability defaults to phase envelope (first phase -> last phase).
                const starts = phases.map(p => moment(p.start_at)).filter((m) => m.isValid());
                const stops = phases.map(p => moment(p.stop_at)).filter((m) => m.isValid());
                if (starts.length && stops.length) {
                    start_at = moment.min(starts).clone();
                    stop_at = moment.max(stops).clone();
                }
            }

            if (!start_at || !stop_at) {
                start_at = moment().add(1, 'day').startOf('day').add(10, 'hours');
                stop_at = moment().add(1, 'day').startOf('day').add(18, 'hours');
            }
            fields.push(new Availability({ start_at, stop_at }));
            !preventSubmit && setTimeout(() => dispatch(submit(form)), 10);
        };

        const isValidDate = (currentDate) => {
            if (!phases?.length)
                return true;

            let result = false;

            phases.forEach(phase => {
                const min = moment(phase.start_at);
                min.startOf('day');

                const max = moment(phase.stop_at);
                max.endOf('day');

                result = result || (
                    !currentDate.isBefore(min) &&
                    !currentDate.isAfter(max)
                );
            });

            return result;
        };

        const dt_bounds = phase_envelope_datetime_bounds(phases);

        return (
            <div className="AvailabilitiesField">
                <Row>
                    <Col sm={12}>
                        {availabilities.sort((a, b) => moment(a.start_at).isBefore(moment(b.start_at)) ? 1 : -1).map((a, index) => (
                            <div key={index} className="availability">
                                <div className="d-flex">
                                    <span>{ t('availabilities.from') }</span>
                                    <Field
                                        name={`availabilities.${index}.start_at`}
                                        isValidDate={isValidDate}
                                        min={dt_bounds.min}
                                        max={dt_bounds.max}
                                        maxBoundField={`availabilities.${index}.stop_at`}
                                        component={DateTimePicker}
                                    />
                                </div>
                                <div className="d-flex">
                                    <span>{ t('availabilities.to') }</span>
                                    <Field
                                        name={`availabilities.${index}.stop_at`}
                                        isValidDate={isValidDate}
                                        min={dt_bounds.min}
                                        max={dt_bounds.max}
                                        endOfDayIfMidnight
                                        minBoundField={`availabilities.${index}.start_at`}
                                        component={DateTimePicker}
                                    />
                                </div>
                                <div className="d-flex button-wrapper">
                                    <Button color="danger" onClick={() => remove(index)}>
                                        <i className="fa fa-trash" />&nbsp;
                                        <span>
                                            { t('common.delete') }
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Col>
                </Row>
                <Row>
                    <Col sm={12}>
                        <Button color="info" onClick={add}>
                            <i className="fa fa-plus"></i>&nbsp;
                            {t('availabilities.add_an_availability')}
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <FieldArray name={ name } component={ Component } />
    );
}

export default AvailabilitiesField;
